import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PhotoPromotion = () => {
    // --- State ---
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(false);

    // --- Hooks & Context ---
    // Added userType to determine role
    const { appId, language, userId, userType } = useAppId();
    const lang = language || 'en';

    // --- Refs ---
    const fileInputRef = useRef(null);

    // --- Role Verification Logic ---
    const activeRole = userType || localStorage.getItem('userType') || localStorage.getItem('designation') || localStorage.getItem('role');
    const roleCode = activeRole ? activeRole.toUpperCase() : 'A';
    const isSubAdmin = roleCode === 'SA' || roleCode === 'SUB_ADMIN';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Photo Promotion",
            subtitle: "View and share promotional images",
            uploadedBy: "Uploaded :",
            loadingData: "Loading Photos...",
            noData: "No promotional photos found.",
            btnAdd: "Add Image",
            btnDelete: "Delete",
            compressing: "Compressing..."
        },
        hi: {
            pageTitle: "फोटो प्रचार",
            subtitle: "प्रचार चित्र देखें और साझा करें",
            uploadedBy: "अपलोड :",
            loadingData: "तस्वीरें लोड हो रही हैं...",
            noData: "कोई प्रचार फोटो नहीं मिला।",
            btnAdd: "छवि जोड़ें",
            btnDelete: "हटाएं",
            compressing: "संपीड़ित कर रहा है..."
        },
        gu: {
            pageTitle: "ફોટો પ્રમોશન",
            subtitle: "પ્રમોશનલ છબીઓ જુઓ અને શેર કરો",
            uploadedBy: "અપલોડ :",
            loadingData: "ફોટા લોડ થઈ રહ્યા છે...",
            noData: "કોઈ પ્રમોશનલ ફોટા મળ્યા નથી.",
            btnAdd: "છબી ઉમેરો",
            btnDelete: "કાઢી નાખો",
            compressing: "સંકુચિત કરી રહ્યા છીએ..."
        }
    };

    const txt = t[lang];

    // --- API Fetch Logic ---
    const fetchPhotos = async () => {
        if (!appId) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_prachar_master`,
                { 
                    app_id: appId,
                    prachar_type: "image"
                }
            );

            if (response.data.success && response.data.data && Array.isArray(response.data.data.result)) {
                setPhotos(response.data.data.result);
            } else {
                setPhotos([]);
            }
        } catch (error) {
            console.error("Error fetching photo promotions:", error);
            setPhotos([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPhotos();
    }, [appId]);

    // --- Global Add Action (INSERT API) ---
    const handleAddImage = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click(); 
        }
    };

    // --- NEW: Image Compression Logic ---
    const compressImage = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    
                    // Set maximum dimensions (adjust these if you want higher/lower quality)
                    const MAX_WIDTH = 1200;
                    const MAX_HEIGHT = 1200;
                    let width = img.width;
                    let height = img.height;

                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height *= MAX_WIDTH / width;
                            width = MAX_WIDTH;
                        }
                    } else {
                        if (height > MAX_HEIGHT) {
                            width *= MAX_HEIGHT / height;
                            height = MAX_HEIGHT;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // Compress to JPEG with 0.7 quality (reduces size significantly)
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
                    
                    // Return the raw base64 string
                    resolve(dataUrl.split(',')[1]);
                };
            };
        });
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setLoading(true);

        try {
            // Use the compression function to avoid 413 Payload Too Large
            const compressedBase64 = await compressImage(file);

            const payload = {
                app_id: appId?.toString(),
                prachar_type: "image",
                base64: compressedBase64, 
                content: file.name,   
                create_by: userId?.toString()
            };

            const response = await axios.post(
                `${BASE_URL}/api/ins_prachar_master`,
                payload
            );

            if (response.data.success) {
                await fetchPhotos();
            } else {
                alert("Failed to upload the photo. Server responded but success was false.");
            }
        } catch (error) {
            console.error("Error uploading photo:", error.response ? error.response.data : error.message);
            if (error.response && error.response.status === 413) {
                alert("Error: The image is still too large. Please select a smaller file.");
            } else {
                alert(`An error occurred while uploading the photo: ${error.message}`);
            }
        } finally {
            setLoading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // --- Individual Card Delete Action ---
    const handleDeleteImage = async (id) => {
        if (window.confirm("Are you sure you want to delete this photo?")) {
            try {
                const payload = {
                    app_id: appId?.toString(),
                    id: id?.toString(),
                    delete_by: userId?.toString()
                };

                const response = await axios.post(
                    `${BASE_URL}/api/del_prachar_master`,
                    payload
                );

                if (response.data.success) {
                    fetchPhotos();
                } else {
                    alert("Failed to delete the photo. Server responded but success was false.");
                }
            } catch (error) {
                console.error("Error deleting photo:", error.response ? error.response.data : error.message);
                alert(`An error occurred while deleting the photo: ${error.message}`);
            }
        }
    };

    // --- Loading State ---
    if (loading && photos.length === 0) {
        return (
            <div className="app-container p-4">
                <Header title={txt.pageTitle}/>
                <div className="loader-wrapper">
                    <div className="spinner-modern"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #E5E7EB; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner-modern { width: 50px; height: 50px; border: 4px solid #cbd5e1; border-top: 4px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                    .loader-text { color: #64748b; font-weight: 600; font-size: 1.1rem; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div className='app-container p-4'>

            {/* Header */}
            <Header title={txt.pageTitle} />

            {/* Page Action Bar (Subtitle + Add Button) */}
            <div className="page-action-bar mt-3 mb-4">
                <div className="subtitle-container m-0">
                    <p className="subtitle-text">{txt.subtitle}</p>
                </div>
                
                <div className="global-buttons">
                    <input 
                        type="file" 
                        accept="image/*" 
                        ref={fileInputRef} 
                        style={{ display: 'none' }} 
                        onChange={handleFileChange} 
                    />
                    
                    {/* CONDITIONAL RENDERING FOR ADD BUTTON - Hidden for Sub Admins */}
                    {!isSubAdmin && (
                        <button 
                            className="global-btn btn-add" 
                            onClick={handleAddImage}
                            disabled={loading} // Prevent double clicking while compressing/uploading
                        >
                            <svg fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            {loading ? txt.compressing : txt.btnAdd}
                        </button>
                    )}
                </div>
            </div>

            {/* No Data State */}
            {photos.length === 0 && !loading && (
                <div className="row">
                    <div className="col-12 text-center text-muted p-5 empty-state">
                        <div className="empty-icon">🖼️</div>
                        <p>{txt.noData}</p>
                    </div>
                </div>
            )}

            {/* --- PHOTO GRID --- */}
            <div className="photo-grid pb-5">
                {photos.map((photo) => (
                    <div className="photo-card" key={photo.id}>
                        {/* Image Container */}
                        <div className="image-wrapper">
                            <img 
                                src={photo.content} 
                                alt={photo.name} 
                                className="promo-image"
                                loading="lazy"
                                onError={(e) => {
                                    e.target.onerror = null; 
                                    e.target.src = "https://via.placeholder.com/400x300?text=Image+Not+Found";
                                }}
                            />
                        </div>

                        {/* Uploader Details Container */}
                        <div className="info-wrapper">
                            <div className="upload-info">
                                <span className="upload-label">{txt.uploadedBy}</span>
                                <span className="upload-name">{photo.name || "Unknown"}</span>
                            </div>
                            <div className="designation-badge">
                                {photo.designation || "User"}
                            </div>

                            {/* Individual Delete Button inside Card - Hidden for Sub Admins */}
                            {!isSubAdmin && (
                                <>
                                    <div className="action-divider"></div>
                                    <button 
                                        className="card-delete-btn" 
                                        onClick={(e) => {
                                            e.stopPropagation(); 
                                            handleDeleteImage(photo.id);
                                        }}
                                    >
                                        <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                            <polyline points="3 6 5 6 21 6"></polyline>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            <line x1="10" y1="11" x2="10" y2="17"></line>
                                            <line x1="14" y1="11" x2="14" y2="17"></line>
                                        </svg>
                                        {txt.btnDelete}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- PAGE ACTION BAR --- */
                .page-action-bar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .subtitle-text {
                    font-size: 15px;
                    color: #475569;
                    font-weight: 600;
                    margin: 0;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .global-buttons {
                    display: flex;
                    gap: 12px;
                }

                .global-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 16px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: none;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                
                .global-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .global-btn svg {
                    width: 16px;
                    height: 16px;
                }

                /* Add Button Global (Solid Blue Theme) */
                .btn-add {
                    background: #3b82f6;
                    color: #ffffff;
                    border: 1px solid #2563eb;
                    box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.2);
                }
                .btn-add:hover:not(:disabled) {
                    background: #2563eb;
                    border-color: #1d4ed8;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 8px -1px rgba(59, 130, 246, 0.3);
                }
                .btn-add:active:not(:disabled) {
                    background: #1d4ed8;
                    transform: translateY(0);
                }

                /* --- PHOTO GRID --- */
                .photo-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 24px;
                }

                /* --- PREMIUM CARD STYLES --- */
                .photo-card {
                    background: #ffffff;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    display: flex;
                    flex-direction: column;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .photo-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.1);
                    border-color: #e2e8f0;
                }

                /* Image Styling */
                .image-wrapper {
                    width: 100%;
                    height: 220px;
                    background: #f8fafc;
                    overflow: hidden;
                    position: relative;
                }

                .promo-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.5s ease;
                }

                .photo-card:hover .promo-image {
                    transform: scale(1.05);
                }

                /* Bottom Info Area */
                .info-wrapper {
                    padding: 16px 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    background: #ffffff;
                }

                .upload-info {
                    display: flex;
                    align-items: baseline;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .upload-label {
                    font-size: 13px;
                    color: #64748b;
                    font-weight: 500;
                }

                .upload-name {
                    font-size: 15px;
                    color: #0f172a;
                    font-weight: 700;
                }

                /* Designation Badge */
                .designation-badge {
                    align-self: flex-start;
                    font-size: 11px;
                    font-weight: 700;
                    color: #3b82f6;
                    background: #eff6ff;
                    padding: 4px 10px;
                    border-radius: 20px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    border: 1px solid #bfdbfe;
                }

                /* --- CARD DELETE BUTTON --- */
                .action-divider {
                    height: 1px;
                    background: #f1f5f9;
                    margin: 8px 0 4px 0;
                }

                .card-delete-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    width: 100%;
                    padding: 8px;
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: none;
                    background: #fef2f2;
                    color: #ef4444;
                }

                .card-delete-btn:hover {
                    background: #fee2e2;
                    color: #dc2626;
                }

                .card-delete-btn:active {
                    background: #fecaca;
                }

                .card-delete-btn svg {
                    width: 16px;
                    height: 16px;
                }

                /* Empty State */
                .empty-state { 
                    background: white; 
                    border-radius: 20px; 
                    border: 2px dashed #cbd5e1; 
                }
                .empty-icon { 
                    font-size: 3rem; 
                    margin-bottom: 10px; 
                    opacity: 0.5; 
                }

                /* Mobile Optimization */
                @media (max-width: 640px) {
                    .page-action-bar {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }
                    .global-buttons {
                        width: 100%;
                        display: flex;
                    }
                    .global-btn {
                        flex: 1;
                        justify-content: center;
                    }
                    .photo-grid { 
                        grid-template-columns: 1fr; 
                        gap: 16px;
                    }
                    .image-wrapper {
                        height: 250px;
                    }
                }
            `}</style>
        </div>
    );
};

export default PhotoPromotion;